/**
 * "How it works" as a simplified architecture diagram: the agent speaks
 * MCP into our AWS serverless stack, lambdas write DynamoDB/S3 and call
 * the provider APIs, the message lands on the owner's phone — and the
 * reply rides provider webhooks back through the stack to a live
 * WebSocket push. Honest to the real architecture, minus the boring
 * parts.
 *
 * Pure SVG, themed via .ad-* classes in styles.css so it follows
 * dark/light. Horizontally scrollable below ~900px (`.arch-wrap`).
 */
import { CHANNELS, glyphColorFor } from './channels';

const LIVE = CHANNELS.filter((c) => c.available);

/** One labelled node box. */
function Node({
  x,
  y,
  w,
  h,
  title,
  sub,
  accent = false,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={10}
        className={accent ? 'ad-node ad-node-accent' : 'ad-node'}
      />
      <text x={x + w / 2} y={y + (sub ? 24 : h / 2 + 5)} textAnchor="middle" fontSize={13.5} fontWeight={700} className="ad-text">
        {title}
      </text>
      {sub && (
        <text x={x + w / 2} y={y + 41} textAnchor="middle" fontSize={10.5} className="ad-muted">
          {sub}
        </text>
      )}
    </g>
  );
}

export function ArchDiagram() {
  const chipX = 810;
  const chipW = 122;
  const chipH = 32;
  const chipGap = 10;
  const chipsTop = 78;

  return (
    <div className="arch-wrap">
      <svg viewBox="0 0 1120 425" xmlns="http://www.w3.org/2000/svg" role="img"
        aria-label="Architecture: your AI agent speaks MCP over HTTPS to Vocal Cord's AWS lambdas, which store messages in DynamoDB and S3 and deliver through provider APIs to your apps; replies come back through provider webhooks and are pushed live to the agent over WebSocket.">
        <defs>
          <marker id="ad-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 1 L 9 5 L 0 9" className="ad-arrowhead" />
          </marker>
        </defs>

        {/* ---------------- your AI agent ---------------- */}
        <rect x={20} y={140} width={190} height={180} rx={14} className="ad-box" />
        <text x={36} y={172} fontSize={15} fontWeight={700} className="ad-text">Your AI</text>
        <text x={36} y={191} fontSize={11} className="ad-muted">any MCP client</text>
        {['Claude Code', 'Cursor', 'OpenClaw', '…'].map((name, i) => (
          <g key={name}>
            <rect x={36} y={206 + i * 26} width={158} height={20} rx={6} className="ad-chip" />
            <text x={44} y={220 + i * 26} fontSize={11} className="ad-text">{name}</text>
          </g>
        ))}

        {/* agent ↔ AWS: forward on top, reply underneath */}
        <line x1={210} y1={185} x2={316} y2={185} className="ad-line-accent" strokeWidth={1.8} markerEnd="url(#ad-arrow)" />
        <text x={263} y={170} textAnchor="middle" fontSize={13} fontWeight={700} className="ad-accent">MCP</text>
        <text x={262} y={207} textAnchor="middle" fontSize={10} className="ad-muted">HTTPS · SendMessage</text>
        <line x1={316} y1={240} x2={210} y2={240} className="ad-line-accent ad-dash" strokeWidth={1.8} markerEnd="url(#ad-arrow)" />
        <text x={262} y={260} textAnchor="middle" fontSize={10} className="ad-muted">live push</text>
        <text x={262} y={273} textAnchor="middle" fontSize={10} className="ad-muted">WebSocket</text>

        {/* ---------------- Vocal Cord on AWS ---------------- */}
        <rect x={320} y={62} width={380} height={342} rx={16} className="ad-aws" />
        <text x={340} y={92} fontSize={15} fontWeight={700} className="ad-text">Vocal Cord</text>
        <rect x={433} y={78} width={102} height={19} rx={9.5} className="ad-chip" />
        <text x={484} y={91.5} textAnchor="middle" fontSize={10.5} className="ad-muted">AWS · serverless</text>

        <Node x={345} y={150} w={150} h={54} title="API Gateway" sub="HTTPS + WebSocket" />
        <Node x={545} y={150} w={142} h={54} title="Lambda" sub="Rust handlers" accent />
        <Node x={345} y={296} w={150} h={54} title="DynamoDB" sub="messages · accounts" />
        <Node x={545} y={296} w={142} h={54} title="S3" sub="attachments" />

        {/* API GW → Lambda */}
        <line x1={495} y1={177} x2={541} y2={177} className="ad-line" strokeWidth={1.5} markerEnd="url(#ad-arrow)" />
        {/* Lambda → DynamoDB */}
        <line x1={563} y1={204} x2={444} y2={292} className="ad-line" strokeWidth={1.5} markerEnd="url(#ad-arrow)" />
        {/* Lambda → S3 */}
        <line x1={616} y1={204} x2={616} y2={292} className="ad-line" strokeWidth={1.5} markerEnd="url(#ad-arrow)" />


        {/* AWS ↔ providers: deliver on top, webhooks back underneath */}
        <line x1={700} y1={185} x2={806} y2={185} className="ad-line-accent" strokeWidth={1.8} markerEnd="url(#ad-arrow)" />
        <text x={753} y={170} textAnchor="middle" fontSize={12} fontWeight={700} className="ad-accent">provider APIs</text>
        <text x={753} y={207} textAnchor="middle" fontSize={10} className="ad-muted">SES · bot APIs</text>
        <line x1={806} y1={240} x2={704} y2={240} className="ad-line-accent ad-dash" strokeWidth={1.8} markerEnd="url(#ad-arrow)" />
        <text x={753} y={260} textAnchor="middle" fontSize={10} className="ad-muted">your reply</text>
        <text x={753} y={273} textAnchor="middle" fontSize={10} className="ad-muted">webhooks</text>

        {/* ---------------- channels ---------------- */}
        {LIVE.map((c, i) => {
          const y = chipsTop + i * (chipH + chipGap);
          const fill = glyphColorFor(c.brandColor);
          const tint =
            fill === 'var(--fg)'
              ? 'color-mix(in srgb, var(--fg) 12%, transparent)'
              : `color-mix(in srgb, ${c.brandColor} 22%, transparent)`;
          return (
            <g key={c.kind}>
              <rect x={chipX} y={y} width={chipW} height={chipH} rx={16} className="ad-node" />
              <circle cx={chipX + 17} cy={y + chipH / 2} r={11} fill={tint} />
              {c.logoPath ? (
                <path
                  d={c.logoPath}
                  fill={fill}
                  transform={`translate(${chipX + 17 - 7.2}, ${y + chipH / 2 - 7.2}) scale(0.6)`}
                />
              ) : (
                <text x={chipX + 17} y={y + chipH / 2 + 4} textAnchor="middle" fontSize={10} fontWeight={700} fill={fill}>
                  {c.letterMark ?? c.displayName[0]}
                </text>
              )}
              <text x={chipX + 34} y={y + chipH / 2 + 4} fontSize={11.5} className="ad-text">
                {c.displayName}
              </text>
            </g>
          );
        })}

        {/* channels ↔ you */}
        <line x1={936} y1={185} x2={968} y2={185} className="ad-line" strokeWidth={1.5} markerEnd="url(#ad-arrow)" />
        <line x1={968} y1={240} x2={936} y2={240} className="ad-line-accent ad-dash" strokeWidth={1.8} markerEnd="url(#ad-arrow)" />

        {/* ---------------- you ---------------- */}
        <rect x={972} y={128} width={118} height={186} rx={20} className="ad-phone" />
        <rect x={1011} y={140} width={40} height={5} rx={2.5} className="ad-chip" />
        <text x={1031} y={212} textAnchor="middle" fontSize={15} fontWeight={700} className="ad-text">You</text>
        <text x={1031} y={232} textAnchor="middle" fontSize={10.5} className="ad-muted">reply from any app</text>

      </svg>
    </div>
  );
}
